"use client";

import { IconCode2Outline18 } from "nucleo-ui-outline-18";
import { RadioGroupItem } from "@/components/ui/radio-group";
import { getSyntaxHighlightingStyle } from "@/lib/syntax-highlighting-style";
import { useColorMode } from "@chakra-ui/react";
import { useMemo, useState } from "react";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { AUTO_GENERATED_TRIGGER_FIELDS } from "./sampleGenerators";
function formatFieldValue(value: unknown): string {
  if (typeof value === "string") {
    if (value.length > 50) return value.slice(0, 47) + "...";
    return value;
  }
  if (typeof value === "number") return String(value);
  if (typeof value === "boolean") return String(value);
  if (Array.isArray(value)) return `[${value.length} items]`;
  if (typeof value === "object" && value !== null) return "{...}";
  return String(value);
}

/**
 * Extract a short human-readable summary from a data row.
 * Picks 2-3 meaningful fields to display as key: value pairs.
 */
function summarizeDataRow(data: Record<string, unknown>): Array<{
  label: string;
  value: string;
}> {
  const entries: Array<{
    label: string;
    value: string;
  }> = [];
  const priorityKeys = ["question", "title", "side", "price", "size", "amount", "slug", "conditionId", "marketId", "id", "name", "message", "body", "timestamp"];

  // Flatten one level: if a top-level value is an object, pull its fields up
  const flat: Record<string, unknown> = {};
  for (const [key, val] of Object.entries(data)) {
    if (AUTO_GENERATED_TRIGGER_FIELDS.includes(key)) continue;
    if (typeof val === "object" && val !== null && !Array.isArray(val)) {
      for (const [subKey, subVal] of Object.entries(val as Record<string, unknown>)) {
        flat[subKey] ??= subVal;
      }
    } else {
      flat[key] = val;
    }
  }
  for (const key of priorityKeys) {
    if (entries.length >= 3) break;
    if (key in flat && flat[key] !== undefined && flat[key] !== null && flat[key] !== "") {
      entries.push({
        label: key,
        value: formatFieldValue(flat[key])
      });
    }
  }
  if (entries.length < 2) {
    const usedKeys = new Set(entries.map(e => e.label));
    for (const [key, val] of Object.entries(flat)) {
      if (entries.length >= 3) break;
      if (usedKeys.has(key)) continue;
      if (val === undefined || val === null || val === "") continue;
      entries.push({
        label: key,
        value: formatFieldValue(val)
      });
    }
  }
  return entries;
}

/** A single data row with summary and run button. */
export default function DataRow({
  data,
  label,
  value,
  selected,
  onSelect
}: {
  data: Record<string, unknown>;
  label?: string;
  value: string;
  selected: boolean;
  onSelect: (value: string) => void;
}) {
  const [showRaw, setShowRaw] = useState(false);
  const {
    colorMode
  } = useColorMode();
  const isDark = colorMode === "dark";
  const summary = useMemo(() => summarizeDataRow(data), [data]);
  const syntaxStyle = useMemo(() => getSyntaxHighlightingStyle(isDark), [isDark]);
  return <div>
      <div className="flex items-center gap-4 py-3 transition-colors">
        <RadioGroupItem value={value} checked={selected} onClick={() => onSelect(value)} className="flex-shrink-0" />
        <button type="button" onClick={() => onSelect(value)} className="min-w-0 flex-1 text-left">
          {label && <span className="label label-xs">{label}</span>}
          {summary.length > 0 && <div className="mt-1 flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1">
              {summary.map(field => <span key={field.label} className="body-sm truncate">
                  <span className="text-ds-text-muted dark:text-ds-text-muted font-medium">{field.label}:</span>{" "}
                  {field.value}
                </span>)}
            </div>}
        </button>
        <button type="button" onClick={() => setShowRaw(!showRaw)} className="text-ds-text-disabled hover:bg-ds-surface-hover hover:text-ds-text-subtle dark:hover:bg-ds-neutral-800 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg transition-colors dark:hover:text-zinc-300" title={showRaw ? "Hide JSON" : "View JSON"}>
          <IconCode2Outline18 className="h-4 w-4" />
        </button>
      </div>
      {showRaw && <div className="border-ds-border-default dark:bg-ds-neutral-900/50 mb-1 ml-8 rounded-lg border bg-zinc-50/50">
          <div className="max-h-48 overflow-y-auto">
            <SyntaxHighlighter language="json" style={syntaxStyle} customStyle={{
          margin: 0,
          padding: "0.75rem",
          fontSize: "0.75rem",
          lineHeight: "1.5",
          background: "transparent"
        }} wrapLines wrapLongLines>
              {JSON.stringify(data, null, 2)}
            </SyntaxHighlighter>
          </div>
        </div>}
    </div>;
}